/** Simple, transparent money maths used by the calculators. */

export const monthlyRate = (annualPercent: number): number => annualPercent / 100 / 12

export const growthSeries = (start: number, monthly: number, annualPercent: number, months: number): Array<number> => {
    const rate = monthlyRate(annualPercent)
    const series = [start]
    let balance = start

    for (let month = 1; month <= months; month += 1) {
        balance = balance * (1 + rate) + monthly
        series.push(balance)
    }

    return series
}

export const monthsToReach = (start: number, monthly: number, annualPercent: number, goal: number, cap = 600): number => {
    if (start >= goal) return 0

    const rate = monthlyRate(annualPercent)
    let balance = start

    for (let month = 1; month <= cap; month += 1) {
        balance = balance * (1 + rate) + monthly
        if (balance >= goal) return month
    }

    return Infinity
}

export interface Amortization {
    feasible: boolean
    months: number
    totalInterest: number
    series: Array<number>
}

export const amortize = (balance: number, annualPercent: number, payment: number, cap = 720): Amortization => {
    const rate = monthlyRate(annualPercent)

    if (balance <= 0) return { feasible: true, months: 0, totalInterest: 0, series: [0] }
    if (payment <= balance * rate + 0.01) return { feasible: false, months: Infinity, totalInterest: Infinity, series: [balance] }

    let remaining = balance
    let interest = 0
    let months = 0
    const series = [balance]

    while (remaining > 0.005 && months < cap) {
        const charge = remaining * rate

        interest += charge
        remaining = remaining + charge - payment
        months += 1
        series.push(Math.max(0, remaining))
    }

    return { feasible: remaining <= 0.005, months, totalInterest: interest, series }
}

/** Linearly resamples a series to an exact number of points for charting. */
export const resample = (series: Array<number>, count: number): Array<number> => {
    if (series.length === 0) return Array.from({ length: count }, () => 0)
    if (series.length === 1) return Array.from({ length: count }, () => series[0])

    return Array.from({ length: count }, (_, index) => {
        const position = (index / (count - 1)) * (series.length - 1)
        const low = Math.floor(position)
        const high = Math.min(series.length - 1, low + 1)
        const weight = position - low

        return series[low] * (1 - weight) + series[high] * weight
    })
}

/** A tidy upper bound for a chart axis, for example 87,400 becomes 100,000. */
export const niceCeiling = (value: number): number => {
    if (value <= 0) return 1

    const magnitude = Math.pow(10, Math.floor(Math.log10(value)))
    const steps = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]
    const normalized = value / magnitude

    return (steps.find(step => step >= normalized) ?? 10) * magnitude
}
